import { useMemo, useState, type JSX, type ReactNode } from "react";
import {
  Button,
  Card,
  DataTable,
  Inline,
  Stack,
  StatePill,
  TextInput,
  type DataTableColumn,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import { formatTimestampSafe } from "../../../locale-format.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiSend } from "../../../lib/api.js";
import {
  auditStateString,
  useGtccApprovalDecisions,
  useGtccPendingDevices,
  type GtccApprovalAuditEntry,
  type GtccApprovalDecision,
  type GtccPendingDevice,
} from "./useGtccDeviceApprovals.js";

/**
 * B-GTCC-A17 — register row 28 ("Device Approval (freigeben, ablehnen)",
 * ticket A-GTCC-13), including the measured approval decisions.
 *
 * What is measured rather than assumed:
 *
 *   1. The open queue and the decided history come from two different
 *      places. `GET /v1/admin/devices/pending` answers only
 *      `pending_approval` rows; the moment a device is approved or
 *      rejected it leaves that list and the decision survives solely as
 *      an audit row. So "offene und entschiedene Freigaben" is one queue
 *      query plus two filtered audit queries, not one endpoint.
 *
 *   2. Their permissions differ, and so does their reach. The queue is
 *      platform-owner and cross-tenant; the audit list is `tenant.read`
 *      and scoped to the calling tenant. A single merged count would
 *      therefore be a lie, so the three lists stay separate, each with
 *      its own loading, error and empty outcome, and the decided
 *      sections carry an explicit scope note.
 *
 *   3. The decided rows have no IMEI column to read. The service writes
 *      the device's IMEI into both audit snapshots
 *      (`device-approval.service.ts:386`), so the table narrows it out
 *      of `afterState` / `beforeState` and falls back to the target id
 *      for historical rows that pre-date the snapshot.
 *
 * The same platform-owner endpoints that power `DeviceApprovalPage` make the
 * pending-row decisions. A successful decision invalidates the complete
 * device-approval query family, so the open queue and both audit lists show
 * the server's next state instead of a locally guessed one.
 */

const EMPTY_PENDING: readonly GtccPendingDevice[] = [];
const EMPTY_DECISIONS: readonly GtccApprovalAuditEntry[] = [];

function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

function SectionCard({
  testId,
  headingId,
  title,
  subtitle,
  children,
}: {
  readonly testId: string;
  readonly headingId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <Card elevation={1}>
        <Stack gap={3}>
          <Stack gap={1}>
            <h3 id={headingId} style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {title}
            </h3>
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>{subtitle}</p>
          </Stack>
          {children}
        </Stack>
      </Card>
    </section>
  );
}

/** The most readable tenant identity the queue row actually carries. */
function tenantLabel(row: GtccPendingDevice, unset: string): string {
  if (row.tenantDisplayName) return row.tenantDisplayName;
  if (row.tenantSlug) return row.tenantSlug;
  if (row.tenantId) return row.tenantId;
  return unset;
}

function PendingDeviceActions({ row }: { readonly row: GtccPendingDevice }): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["gtcc", "device-approval"] });
  };
  const approve = useMutation({
    mutationFn: () =>
      apiSend("POST", `/v1/admin/devices/${row.id}/approve`, { tenantId: row.tenantId! }),
    onSuccess: invalidate,
    onError: () => setError(t("admin.gtcc.device_approval.write.error")),
  });
  const reject = useMutation({
    mutationFn: () =>
      apiSend("POST", `/v1/admin/devices/${row.id}/reject`, { tenantId: row.tenantId!, reason }),
    onSuccess: () => {
      setRejectOpen(false);
      setReason("");
      invalidate();
    },
    onError: () => setError(t("admin.gtcc.device_approval.write.error")),
  });
  const disabled = !row.tenantId || approve.isPending || reject.isPending;
  return (
    <Stack gap={1}>
      <Inline gap={1}>
        <Button
          variant="primary"
          disabled={disabled}
          onClick={() => {
            setError(null);
            approve.mutate();
          }}
          data-testid={`gtcc-device-approve-${row.id}`}
        >
          {t("admin.gtcc.device_approval.write.approve")}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => setRejectOpen((open) => !open)}
          data-testid={`gtcc-device-reject-${row.id}`}
        >
          {t("admin.gtcc.device_approval.write.reject")}
        </Button>
      </Inline>
      {rejectOpen ? (
        <Inline gap={1}>
          <TextInput
            label={t("admin.gtcc.device_approval.write.reason")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button
            variant="primary"
            disabled={disabled || !reason.trim()}
            onClick={() => {
              setError(null);
              reject.mutate();
            }}
            data-testid={`gtcc-device-reject-confirm-${row.id}`}
          >
            {t("admin.gtcc.device_approval.write.confirm")}
          </Button>
        </Inline>
      ) : null}
      {error ? <span role="alert">{error}</span> : null}
    </Stack>
  );
}

export function GtccDeviceApprovalView(): JSX.Element {
  const { t, locale } = useLocale();

  const pending = useGtccPendingDevices();
  const approved = useGtccApprovalDecisions("device.approve");
  const rejected = useGtccApprovalDecisions("device.reject");

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }),
    [locale],
  );

  const unset = t("admin.gtcc.device_approval.value.unset");

  const timestamp = (value: string | null | undefined): ReactNode =>
    value ? (
      <time dateTime={value}>{formatTimestampSafe(value, (d) => dateFormatter.format(d))}</time>
    ) : (
      unset
    );

  const pendingColumns: readonly DataTableColumn<GtccPendingDevice>[] = [
    {
      id: "status",
      header: t("admin.gtcc.device_approval.column.status"),
      cell: () => <StatePill tone="warn" label={t("admin.gtcc.device_approval.status.pending")} />,
    },
    {
      id: "device",
      header: t("admin.gtcc.device_approval.column.device"),
      cell: (row) => (
        <Stack gap={0}>
          <span style={{ fontFamily: "monospace" }}>{row.imei}</span>
          <span style={{ color: "var(--gt-sys-text-muted)" }}>{row.deviceType}</span>
        </Stack>
      ),
    },
    {
      id: "tenant",
      header: t("admin.gtcc.device_approval.column.tenant"),
      cell: (row) => tenantLabel(row, unset),
    },
    {
      id: "registered_by",
      header: t("admin.gtcc.device_approval.column.registered_by"),
      cell: (row) => row.registeredByEmail ?? row.registeredBySub ?? unset,
    },
    {
      id: "time",
      header: t("admin.gtcc.device_approval.column.requested_at"),
      cell: (row) => timestamp(row.createdAt),
    },
    {
      id: "actions",
      header: t("admin.gtcc.device_approval.write.actions"),
      cell: (row) => <PendingDeviceActions row={row} />,
    },
  ];

  const decisionColumns = (
    decision: GtccApprovalDecision,
  ): readonly DataTableColumn<GtccApprovalAuditEntry>[] => [
    {
      id: "status",
      header: t("admin.gtcc.device_approval.column.status"),
      cell: () =>
        decision === "device.approve" ? (
          <StatePill tone="success" label={t("admin.gtcc.device_approval.status.approved")} />
        ) : (
          <StatePill tone="danger" label={t("admin.gtcc.device_approval.status.rejected")} />
        ),
    },
    {
      id: "device",
      header: t("admin.gtcc.device_approval.column.device"),
      cell: (row) => (
        <span style={{ fontFamily: "monospace" }}>
          {auditStateString(row.afterState, "imei") ??
            auditStateString(row.beforeState, "imei") ??
            row.targetId}
        </span>
      ),
    },
    {
      id: "tenant",
      header: t("admin.gtcc.device_approval.column.tenant"),
      cell: (row) => row.tenantId,
    },
    {
      id: "decided_by",
      header: t("admin.gtcc.device_approval.column.decided_by"),
      cell: (row) => row.actorEmail || row.actorSub || unset,
    },
    {
      id: "time",
      header: t("admin.gtcc.device_approval.column.decided_at"),
      cell: (row) => timestamp(row.createdAt),
    },
    {
      id: "reason",
      header: t("admin.gtcc.device_approval.column.reason"),
      cell: (row) => auditStateString(row.afterState, "rejectedReason") ?? unset,
    },
  ];

  const pendingRows = pending.data?.items ?? EMPTY_PENDING;
  const pendingError = pending.isError
    ? {
        title: isForbidden(pending.error)
          ? t("admin.gtcc.device_approval.pending.forbidden")
          : t("admin.gtcc.device_approval.pending.error"),
      }
    : null;

  const decisionList = (
    decision: GtccApprovalDecision,
    slot: "approved" | "rejected",
    query: ReturnType<typeof useGtccApprovalDecisions>,
  ): JSX.Element => {
    const rows = query.data?.items ?? EMPTY_DECISIONS;
    const error = query.isError
      ? {
          title: isForbidden(query.error)
            ? t("admin.gtcc.device_approval.decision.forbidden")
            : t(`admin.gtcc.device_approval.${slot}.error`),
        }
      : null;

    return (
      <SectionCard
        testId={`gtcc-device-approval-${slot}`}
        headingId={`gtcc-device-approval-${slot}-heading`}
        title={t(`admin.gtcc.device_approval.${slot}.title`)}
        subtitle={t(`admin.gtcc.device_approval.${slot}.subtitle`)}
      >
        {query.isSuccess ? (
          <Inline gap={2}>
            <StatePill
              tone={slot === "approved" ? "success" : "danger"}
              label={t("admin.gtcc.device_approval.count", { count: rows.length })}
            />
            {query.data.nextCursor ? (
              <StatePill tone="neutral" label={t("admin.gtcc.device_approval.truncated")} />
            ) : null}
            <StatePill tone="info" label={t("admin.gtcc.device_approval.decision.scope")} />
          </Inline>
        ) : null}
        <DataTable
          caption={t(`admin.gtcc.device_approval.${slot}.table_caption`)}
          columns={decisionColumns(decision)}
          rows={rows}
          getRowId={(row) => row.id}
          loading={query.isPending}
          loadingLabel={t(`admin.gtcc.device_approval.${slot}.loading`)}
          error={error}
          empty={{ title: t(`admin.gtcc.device_approval.${slot}.empty`) }}
          testId={`gtcc-device-approval-${slot}-table`}
        />
      </SectionCard>
    );
  };

  return (
    <Stack
      gap={4}
      data-testid="gtcc-view-device-approval"
      style={{ padding: "var(--gt-sys-space-5)" }}
    >
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.device_approval.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.device_approval.subtitle")}
        </p>
        <StatePill tone="info" label={t("admin.gtcc.device_approval.write.enabled")} />
      </Stack>

      <SectionCard
        testId="gtcc-device-approval-pending"
        headingId="gtcc-device-approval-pending-heading"
        title={t("admin.gtcc.device_approval.pending.title")}
        subtitle={t("admin.gtcc.device_approval.pending.subtitle")}
      >
        {pending.isSuccess ? (
          <Inline gap={2}>
            <StatePill
              tone={pendingRows.length > 0 ? "warn" : "success"}
              label={t("admin.gtcc.device_approval.count", { count: pendingRows.length })}
            />
            <StatePill tone="info" label={t("admin.gtcc.device_approval.pending.scope")} />
            {/* PAKET 003240 — a full page is not a complete queue: when the
                route reports `truncated`, the count pill is accompanied by
                the same neutral pill the decided lists already use. */}
            {pending.data.truncated === true ? (
              <StatePill
                tone="neutral"
                label={t("admin.gtcc.device_approval.pending.truncated")}
                dataTestId="gtcc-device-approval-pending-truncated"
              />
            ) : null}
          </Inline>
        ) : null}
        <DataTable
          caption={t("admin.gtcc.device_approval.pending.table_caption")}
          columns={pendingColumns}
          rows={pendingRows}
          getRowId={(row) => row.id}
          loading={pending.isPending}
          loadingLabel={t("admin.gtcc.device_approval.pending.loading")}
          error={pendingError}
          empty={{ title: t("admin.gtcc.device_approval.pending.empty") }}
          testId="gtcc-device-approval-pending-table"
        />
      </SectionCard>

      {decisionList("device.approve", "approved", approved)}
      {decisionList("device.reject", "rejected", rejected)}

      <SectionCard
        testId="gtcc-device-approval-writes"
        headingId="gtcc-device-approval-writes-heading"
        title={t("admin.gtcc.device_approval.writes.title")}
        subtitle={t("admin.gtcc.device_approval.writes.subtitle")}
      >
        <StatePill tone="success" label={t("admin.gtcc.device_approval.write.enabled")} />
        <p style={{ margin: 0 }}>{t("admin.gtcc.device_approval.writes.body")}</p>
      </SectionCard>
    </Stack>
  );
}
